import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import Loading from '../../../components/Loading';
import Modal from '../../../components/Modal/Modal';
import usePermissions from '../../../components/Menu/MenuItems/hooks/usePermissions';
import ClienteStatusBadge from '../components/ClienteStatusBadge';
import AddressFormModal from './components/AddressFormModal';
import {
   getCustomer,
   deleteCustomer,
   updateAddress,
   deleteAddress,
} from '../../../services/customers/customersApi';
import { formatDocForDisplay } from '../../../utils/format';
import { formatPhoneForDisplay, formatCep } from '../utils/format';
import { Container } from '../../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderText,
   PageTitle,
   PageSubtitle,
   PageHeaderActions,
} from '../../../styles/layout/PageHeader';
import {
   Section,
   SectionBody,
   SectionHeader,
   SectionTitle,
   SectionActions,
} from '../../../styles/layout/Section';
import { EmptyState } from '../../../styles/feedback/EmptyState';

// ─── Estilos locais ──────────────────────────────────────────────────────────

const DataGrid = styled.div`
   display: grid;
   grid-template-columns: repeat(3, 1fr);
   gap: 16px;

   @media (max-width: 768px) {
      grid-template-columns: 1fr;
   }
`;

const DataItem = styled.div`
   display: flex;
   flex-direction: column;
   gap: 2px;

   span.label {
      font-size: 0.7rem;
      font-weight: 700;
      color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};
      text-transform: uppercase;
   }
   span.value {
      font-size: 0.9rem;
      color: ${({ theme }) => theme?.colors?.text ?? '#111'};
   }
`;

const AddressList = styled.div`
   display: flex;
   flex-direction: column;
   gap: 10px;
`;

const AddressCard = styled.div`
   border: 1px solid
      ${({ $primary, theme }) =>
         $primary
            ? 'rgba(46,125,50,0.45)'
            : theme?.colors?.border ?? 'rgba(0,0,0,0.1)'};
   background: ${({ $primary }) =>
      $primary ? 'rgba(46,125,50,0.05)' : 'transparent'};
   border-radius: 10px;
   padding: 12px 14px;
   display: flex;
   justify-content: space-between;
   gap: 12px;
   flex-wrap: wrap;
`;

const AddressInfo = styled.div`
   display: flex;
   flex-direction: column;
   gap: 4px;
   min-width: 0;

   .title {
      font-size: 0.85rem;
      font-weight: 700;
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
   }
   .line {
      font-size: 0.78rem;
      color: ${({ theme }) => theme?.colors?.textMuted ?? '#555'};
   }
`;

const AddressActions = styled.div`
   display: flex;
   gap: 6px;
   align-items: flex-start;
   flex-wrap: wrap;

   button {
      font-size: 12px;
      padding: 4px 10px;
   }
`;

const Pill = styled.span`
   display: inline-flex;
   align-items: center;
   padding: 2px 8px;
   border-radius: 999px;
   font-size: 10px;
   font-weight: 800;
   border: 1px solid ${({ $color }) => $color ?? 'rgba(0,0,0,0.15)'};
   background: ${({ $bg }) => $bg ?? 'rgba(0,0,0,0.06)'};
   color: ${({ $text }) => $text ?? '#444'};
`;

const PRIMARY_PILL = {
   bg: 'rgba(46,125,50,0.12)',
   border: 'rgba(46,125,50,0.35)',
   text: '#1b5e20',
};

const GEOCODE_CONFIG = {
   pending: { label: 'Geo: pendente', bg: 'rgba(0,0,0,0.06)', border: 'rgba(0,0,0,0.15)', text: '#555' },
   ok: { label: 'Geo: ok', bg: 'rgba(46,125,50,0.12)', border: 'rgba(46,125,50,0.35)', text: '#1b5e20' },
   failed: { label: 'Geo: falhou', bg: 'rgba(211,47,47,0.12)', border: 'rgba(211,47,47,0.35)', text: '#b71c1c' },
};

function GeocodeBadge({ status = null }) {
   const cfg = GEOCODE_CONFIG[status];
   if (!cfg) return null;
   return (
      <Pill $bg={cfg.bg} $color={cfg.border} $text={cfg.text}>
         {cfg.label}
      </Pill>
   );
}

GeocodeBadge.propTypes = {
   status: PropTypes.string,
};

// Monta a linha de endereço "Rua A, 100 - Centro · Goiânia/GO · 74000-000".
function buildAddressLine(addr) {
   const street = [addr.street, addr.number].filter(Boolean).join(', ');
   const cityState = [addr.city, addr.state].filter(Boolean).join('/');
   const parts = [
      [street, addr.neighborhood].filter(Boolean).join(' - '),
      cityState,
      addr.postal_code ? formatCep(addr.postal_code) : null,
   ].filter(Boolean);
   return parts.join(' · ');
}

const NO_CONFIRM = { isOpen: false };

export default function ClienteShow() {
   const { id } = useParams();
   const navigate = useNavigate();
   const location = useLocation();
   const { hasPermission } = usePermissions();

   // Voltar para a lista preservando os filtros (URL): se há histórico nesta
   // sessão, navigate(-1) restaura a URL filtrada de onde o usuário veio;
   // senão (deep-link direto no detalhe), cai na lista limpa.
   const handleBack = () => {
      if (location.key !== 'default') navigate(-1);
      else navigate('/comercial/clientes');
   };
   const canUpdate = hasPermission('customers:customer:update');
   const canDelete = hasPermission('customers:customer:delete');

   const [customer, setCustomer] = useState(null);
   const [isLoading, setIsLoading] = useState(false);

   const [addressModal, setAddressModal] = useState({
      isOpen: false,
      address: null,
   });
   // Diálogo de confirmação genérico (excluir cliente / remover endereço).
   const [confirm, setConfirm] = useState(NO_CONFIRM);
   const [isConfirming, setIsConfirming] = useState(false);

   const fetchCustomer = useCallback(async () => {
      setIsLoading(true);
      try {
         const data = await getCustomer(id);
         if (!data) {
            toast.error('Cliente não encontrado.');
            navigate('/comercial/clientes');
            return;
         }
         setCustomer(data);
      } catch (err) {
         const apiMessage =
            err?.response?.data?.errors?.[0]?.message ??
            err?.response?.data?.message ??
            'Erro ao carregar cliente.';
         toast.error(apiMessage);
         navigate('/comercial/clientes');
      } finally {
         setIsLoading(false);
      }
   }, [id, navigate]);

   useEffect(() => {
      fetchCustomer();
   }, [fetchCustomer]);

   // Endereços com o principal no topo.
   const addresses = [...(customer?.addresses ?? [])].sort(
      (a, b) => Number(b.is_primary) - Number(a.is_primary),
   );

   // ─── Ações ────────────────────────────────────────────────────────────────
   const closeConfirm = () => {
      if (isConfirming) return;
      setConfirm(NO_CONFIRM);
   };

   const runConfirm = async () => {
      if (!confirm.onConfirm || isConfirming) return;
      setIsConfirming(true);
      try {
         await confirm.onConfirm();
      } finally {
         setIsConfirming(false);
         setConfirm(NO_CONFIRM);
      }
   };

   const askDeleteCustomer = () => {
      setConfirm({
         isOpen: true,
         title: 'Excluir cliente',
         message: `Tem certeza que deseja excluir "${customer.name}"? O cliente sai das listagens (exclusão reversível no banco).`,
         confirmLabel: 'Sim, excluir cliente',
         onConfirm: async () => {
            try {
               await deleteCustomer(id);
               toast.success('Cliente excluído com sucesso.');
               navigate('/comercial/clientes');
            } catch (err) {
               toast.error(
                  err?.response?.data?.message ?? 'Erro ao excluir cliente.',
               );
            }
         },
      });
   };

   const askDeleteAddress = (address) => {
      setConfirm({
         isOpen: true,
         title: 'Remover endereço',
         message: 'Tem certeza que deseja remover este endereço?',
         confirmLabel: 'Sim, remover',
         onConfirm: async () => {
            try {
               await deleteAddress(id, address.id);
               toast.success('Endereço removido com sucesso.');
               fetchCustomer();
            } catch (err) {
               toast.error(
                  err?.response?.data?.message ?? 'Erro ao remover endereço.',
               );
            }
         },
      });
   };

   const handleMakePrimary = async (address) => {
      try {
         await updateAddress(id, address.id, { is_primary: true });
         toast.success('Endereço principal atualizado.');
         fetchCustomer();
      } catch (err) {
         toast.error(
            err?.response?.data?.message ??
               'Erro ao definir endereço principal.',
         );
      }
   };

   if (!customer) {
      return (
         <Container>
            <Loading isLoading={isLoading} />
         </Container>
      );
   }

   return (
      <Container>
         <Loading isLoading={isLoading} />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>{customer.name}</PageTitle>
               <PageSubtitle>Detalhe do cliente e endereços</PageSubtitle>
            </PageHeaderText>
            <PageHeaderActions>
               <button type="button" onClick={handleBack}>
                  Voltar
               </button>
               {canUpdate && (
                  <button
                     type="button"
                     onClick={() =>
                        navigate(`/comercial/clientes/${id}/editar`)
                     }
                  >
                     Editar
                  </button>
               )}
               {canDelete && (
                  <button type="button" onClick={askDeleteCustomer}>
                     Excluir
                  </button>
               )}
            </PageHeaderActions>
         </PageHeader>

         {/* ─── Dados do cliente ──────────────────────────────────────────── */}
         <Section>
            <SectionHeader>
               <SectionTitle>Dados do cliente</SectionTitle>
            </SectionHeader>
            <SectionBody>
               <DataGrid>
                  <DataItem>
                     <span className="label">CPF/CNPJ</span>
                     <span className="value">
                        {formatDocForDisplay(customer.doc)}
                     </span>
                  </DataItem>
                  <DataItem>
                     <span className="label">Tipo de pessoa</span>
                     <span className="value">{customer.person_type ?? '—'}</span>
                  </DataItem>
                  <DataItem>
                     <span className="label">Status</span>
                     <span className="value">
                        <ClienteStatusBadge value={customer.status} />
                     </span>
                  </DataItem>
                  <DataItem>
                     <span className="label">E-mail</span>
                     <span className="value">{customer.email ?? '—'}</span>
                  </DataItem>
                  <DataItem>
                     <span className="label">Telefone</span>
                     <span className="value">
                        {formatPhoneForDisplay(customer.phone)}
                     </span>
                  </DataItem>
                  <DataItem>
                     <span className="label">Código Refiltek</span>
                     <span className="value">
                        {customer.refiltek_id ?? '—'}
                     </span>
                  </DataItem>
               </DataGrid>
            </SectionBody>
         </Section>

         {/* ─── Endereços ─────────────────────────────────────────────────── */}
         <Section>
            <SectionHeader>
               <SectionTitle>Endereços</SectionTitle>
               <SectionActions>
                  {canUpdate && (
                     <button
                        type="button"
                        onClick={() =>
                           setAddressModal({ isOpen: true, address: null })
                        }
                     >
                        + Adicionar endereço
                     </button>
                  )}
               </SectionActions>
            </SectionHeader>
            <SectionBody>
               {addresses.length > 0 ? (
                  <AddressList>
                     {addresses.map((address) => (
                        <AddressCard
                           key={address.id}
                           $primary={address.is_primary}
                        >
                           <AddressInfo>
                              <div className="title">
                                 {address.label || 'Endereço'}
                                 {address.is_primary && (
                                    <Pill
                                       $bg={PRIMARY_PILL.bg}
                                       $color={PRIMARY_PILL.border}
                                       $text={PRIMARY_PILL.text}
                                    >
                                       Principal
                                    </Pill>
                                 )}
                                 <GeocodeBadge status={address.geocode_status} />
                              </div>
                              <div className="line">
                                 {buildAddressLine(address) || '—'}
                              </div>
                              {address.complement && (
                                 <div className="line">
                                    {address.complement}
                                 </div>
                              )}
                           </AddressInfo>

                           {canUpdate && (
                              <AddressActions>
                                 {!address.is_primary && (
                                    <button
                                       type="button"
                                       onClick={() =>
                                          handleMakePrimary(address)
                                       }
                                    >
                                       Tornar principal
                                    </button>
                                 )}
                                 <button
                                    type="button"
                                    onClick={() =>
                                       setAddressModal({
                                          isOpen: true,
                                          address,
                                       })
                                    }
                                 >
                                    Editar
                                 </button>
                                 <button
                                    type="button"
                                    onClick={() => askDeleteAddress(address)}
                                 >
                                    Remover
                                 </button>
                              </AddressActions>
                           )}
                        </AddressCard>
                     ))}
                  </AddressList>
               ) : (
                  <EmptyState
                     title="Nenhum endereço cadastrado"
                     description={
                        canUpdate
                           ? 'Use "Adicionar endereço" para cadastrar o primeiro.'
                           : 'Este cliente ainda não possui endereços.'
                     }
                  />
               )}
            </SectionBody>
         </Section>

         <AddressFormModal
            isOpen={addressModal.isOpen}
            address={addressModal.address}
            customerId={id}
            onClose={() => setAddressModal({ isOpen: false, address: null })}
            onSaved={fetchCustomer}
         />

         <Modal
            isOpen={confirm.isOpen}
            onClose={closeConfirm}
            title={confirm.title}
            maxWidth="460px"
            footer={
               <>
                  <button
                     type="button"
                     onClick={closeConfirm}
                     disabled={isConfirming}
                  >
                     Cancelar
                  </button>
                  <button
                     type="button"
                     onClick={runConfirm}
                     disabled={isConfirming}
                  >
                     {isConfirming ? 'Processando...' : confirm.confirmLabel}
                  </button>
               </>
            }
         >
            <p style={{ margin: 0, fontSize: '0.9rem' }}>{confirm.message}</p>
         </Modal>
      </Container>
   );
}
